/**
 * Renders a residence's published advisories (editorial incidents /
 * notices) as severity-colored cards. Shared by the residence page, the
 * listing detail drawer, and the analyze result, so the look is identical
 * everywhere. Deliberately i18n-free: advisory copy is admin-authored in
 * one language, and the only chrome (date + source host) is neutral.
 *
 * Colors use the *-50/*-200/*-900 scale, which the dark theme remaps to
 * readable dark surfaces (see globals.css .dark .bg-amber-50 etc.).
 */
import { AlertTriangle, Info, ExternalLink } from "lucide-react";
import type { ResidenceAdvisory } from "@/types";

const STYLES: Record<string, { wrap: string; icon: string }> = {
  critical: { wrap: "bg-rose-50 border-rose-200 text-rose-900", icon: "text-rose-600" },
  warning: { wrap: "bg-amber-50 border-amber-200 text-amber-900", icon: "text-amber-600" },
  info: { wrap: "bg-sky-50 border-sky-200 text-sky-900", icon: "text-sky-600" },
};

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "source";
  }
}

export default function AdvisoryBanner({
  advisories,
  className = "",
}: {
  advisories?: ResidenceAdvisory[] | null;
  className?: string;
}) {
  if (!advisories || advisories.length === 0) return null;
  return (
    <div className={`space-y-2 ${className}`}>
      {advisories.map((a) => {
        const s = STYLES[a.severity] ?? STYLES.info;
        const Icon = a.severity === "info" ? Info : AlertTriangle;
        return (
          <div key={a.id || a.title} className={`rounded-xl border p-3 ${s.wrap}`}>
            <div className="flex items-start gap-2.5">
              <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${s.icon}`} />
              <div className="min-w-0 flex-1">
                {a.title && (
                  <p className="text-sm font-semibold leading-snug">{a.title}</p>
                )}
                {a.body && (
                  <p className="mt-0.5 text-sm leading-relaxed opacity-90 whitespace-pre-line">
                    {a.body}
                  </p>
                )}
                {(() => {
                  // Multiple incident sources; fall back to the legacy
                  // single field. Each renders as its own host chip.
                  const urls = (a.source_urls && a.source_urls.length > 0)
                    ? a.source_urls
                    : (a.source_url ? [a.source_url] : []);
                  if (!a.date && urls.length === 0) return null;
                  return (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs opacity-70">
                      {a.date && <span className="tabular-nums">{a.date}</span>}
                      {urls.map((u, j) => (
                        <a
                          key={j}
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {hostOf(u)}
                        </a>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
